import { useEffect } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useActiveProfile } from '../features/profiles/useActiveProfile';
import { CelebrationEffect } from '../effects/CelebrationEffect';
import { Confetti } from '../effects/Confetti';
import { playFanfare } from '../effects/fanfare';
import { playFromSpec } from '../effects/playFromSpec';
import { useSoundEnabled } from '../effects/useSoundEnabled';
import { getRewardEmoji } from '../features/progress/rewardEmoji';
import { useCustomThemes, useSyncedStore } from '../store/synced';
import { resolveThemeWithLibrary } from '../themes/resolveTheme';

interface CelebrationState {
  stars?: number;
}

// All-done screen. Per plan/03 §Animations & effects: trophy wobble +
// confetti + fanfare. Per plan/03 §Navigation rules: browser back from
// here returns to the task list — the auto-nav from TaskPage pushes
// /celebrate, so back works naturally. The visible "Takaisin" button
// replaces history with the routine selector for the same profile so
// the kid can immediately start a sibling routine without losing
// profile context.
export function CelebrationPage() {
  const { profileId } = useParams();
  const location = useLocation();
  useActiveProfile(profileId);
  const soundOn = useSoundEnabled();
  const profile = useSyncedStore((s) => s.tree?.profiles.find((p) => p.id === profileId));
  const resolvedId = profile?.id;
  const stars = (location.state as CelebrationState | null)?.stars ?? 0;
  const customThemes = useCustomThemes();
  const resolvedTheme = profile
    ? resolveThemeWithLibrary(profile, undefined, customThemes)
    : undefined;
  const customCelebration = resolvedTheme?.customCelebrationSpec;
  const customSound = resolvedTheme?.customSoundSpec;

  useEffect(() => {
    if (!resolvedId || !soundOn) return;
    if (customSound) {
      playFromSpec(customSound, 'celebration');
    } else {
      playFanfare();
    }
  }, [resolvedId, soundOn, customSound]);

  if (!profile) return <Navigate to="/" replace />;

  return (
    <main className="celebrate" style={{ ['--profile-color' as string]: profile.color }}>
      {customCelebration ? (
        <CelebrationEffect spec={customCelebration} palette={resolvedTheme?.routineCardPalette} />
      ) : (
        <Confetti />
      )}
      <div className="celebrate-trophy" aria-hidden="true">
        🏆
      </div>
      <h1 className="celebrate-title">Hienoa työtä!</h1>
      <p className="celebrate-name">
        <span aria-hidden="true">{profile.avatarEmoji}</span> {profile.name}
      </p>
      {stars > 0 && (
        <p className="celebrate-stars" aria-label={`Keräsit ${stars} tähteä`}>
          {getRewardEmoji(profile).repeat(stars)}
        </p>
      )}
      <Link to={`/p/${profile.id}`} replace className="celebrate-button">
        Takaisin
      </Link>
    </main>
  );
}
